import React, { useState, useEffect } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Box,
  Grid
} from '@material-ui/core';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit,
  CompareArrows as CompareIcon,
  AddCircle,
  Delete,
  CheckCircle,
  Cancel,
} from '@material-ui/icons';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColumns,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridSelectionModel,
  GridToolbarContainer,
  GridValidRowModel,
  GridValueFormatterParams,
  MuiEvent
} from '@mui/x-data-grid-pro';
import moment from 'moment';
import { formatNumber } from 'utils/number';
import { AditamentoDiferenca } from './aditamentoRevisionModal';
import { Button } from 'm2m.components';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { v4 as uuidv4 } from 'uuid';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2),
      fontWeight: 600,
      color: theme.palette.primary.main
    },
    comparisonContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden'
    },
    beforeAfterHeader: {
      backgroundColor: theme.palette.grey[50],
      padding: theme.spacing(1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    beforeSection: {
      backgroundColor: theme.palette.error.light + '10',
      borderRight: `1px solid ${theme.palette.divider}`
    },
    afterSection: {
      backgroundColor: theme.palette.success.light + '10'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      fontWeight: 600,
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    beforeHeader: {
      color: theme.palette.error.main,
      '& .MuiSvgIcon-root': {
        color: theme.palette.error.main
      }
    },
    afterHeader: {
      color: theme.palette.success.main,
      '& .MuiSvgIcon-root': {
        color: theme.palette.success.main
      }
    },
    tableContainer: {
      maxHeight: 400,
      '& .MuiTableCell-root': {
        padding: theme.spacing(1, 2),
        fontSize: '0.875rem'
      }
    },
    tableHeader: {
      backgroundColor: theme.palette.grey[100],
      '& .MuiTableCell-head': {
        fontWeight: 600,
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary
      }
    },
    rowAdded: {
      backgroundColor: theme.palette.success.light + '20',
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.success.light}`
      }
    },
    rowRemoved: {
      backgroundColor: theme.palette.error.light + '20',
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.error.light}`
      }
    },
    rowModified: {
      backgroundColor: theme.palette.warning.light + '15',
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.warning.light}`
      }
    },
    changeIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontSize: '0.75rem',
      fontWeight: 600
    },
    addedIndicator: {
      color: theme.palette.success.main
    },
    removedIndicator: {
      color: theme.palette.error.main
    },
    modifiedIndicator: {
      color: theme.palette.warning.main
    },
    valueCell: {
      fontFamily: 'monospace',
      fontSize: '0.8rem'
    },
    emptyState: {
      padding: theme.spacing(3),
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontStyle: 'italic'
    },
    summaryChip: {
      margin: theme.spacing(0.25),
      fontSize: '0.7rem'
    },
    dataGridDiv: {
      height: 400,
      width: '100%',
      '& .MuiDataGrid-root': {
        border: '1px solid #e0e0e0',
        borderRadius: 4,
      },
      '& .MuiDataGrid-cell': {
        padding: '8px 12px',
      },
      '& .MuiDataGrid-columnHeader': {
        fontWeight: 600,
        backgroundColor: '#f5f5f5',
      },
      '& .row-added': {
        backgroundColor: theme.palette.success.light + '20',
      },
      '& .row-modified': {
        backgroundColor: theme.palette.warning.light + '20',
      },
      '& .row-removed': {
        backgroundColor: theme.palette.error.light + '20',
      },
      '& .field-changed': {
        backgroundColor: theme.palette.warning.light + '30',
        fontWeight: 600
      }
    },
    beforeTableContainer: {
      maxHeight: 400,
      overflow: 'auto',
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      marginBottom: theme.spacing(1),
      '& .MuiTableCell-root': {
        padding: '8px 12px',
        fontSize: '0.875rem',
      },
      '& .MuiTableCell-head': {
        fontWeight: 600,
        backgroundColor: '#f5f5f5',
      },
    },
  })
);

interface ArrayChangesComparisonTableProps {
  title: string;
  arrayType: 'amortizacoes' | 'pagamentojuros' | 'incorporacaojuros';
  arrayDifference: AditamentoDiferenca;
  onArrayUpdate?: (updatedArrayDifference: AditamentoDiferenca) => void;
  canEdit?: boolean;
  className?: string;
}

export const ArrayChangesComparisonTable: React.FC<ArrayChangesComparisonTableProps> = ({
  title,
  arrayType,
  arrayDifference,
  onArrayUpdate,
  canEdit = false,
  className
}) => {
  const classes = useStyles();

  const [pageSize, setPageSize] = useState(20);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [beforeRows, setBeforeRows] = useState<any[]>([]);

  const extractArrayData = () => {
    const beforeData: { [key: string]: any } = {};
    const afterData: { [key: string]: any } = {};

    try {
      let oldArray: any[] = [];
      if (arrayDifference.oldField && arrayDifference.oldField !== 'null') {
        oldArray = JSON.parse(arrayDifference.oldField);
      }

      let newArray: any[] = [];
      if (arrayDifference.newField && arrayDifference.newField !== 'null') {
        newArray = JSON.parse(arrayDifference.newField);
      }

      oldArray.forEach((item, index) => {
        beforeData[index] = {
          id: `before-${index}`,
          index,
          ...item
        };
      });

      newArray.forEach((item, index) => {
        afterData[index] = {
          id: `after-${index}`,
          index,
          isNew: false,
          ...item
        };
      });
    } catch (error) {
      console.error('Error parsing array data:', error, arrayDifference);
    }

    return { beforeData, afterData };
  };

  const { beforeData, afterData } = extractArrayData();

  useEffect(() => {
    const afterItems = Object.values(afterData).map(item => ({
      ...item,
      changeType: beforeData[item.index] ?
        (JSON.stringify(beforeData[item.index]) !== JSON.stringify(item) ? 'modified' : 'unchanged') :
        'added'
    }));

    const beforeItems = Object.values(beforeData).map(item => ({
      ...item,
      changeType: afterData[item.index] ? 'modified' : 'removed'
    }));

    setRows(afterItems.sort((a, b) => a.index - b.index));
    setBeforeRows(beforeItems.sort((a, b) => a.index - b.index));

    setRowModesModel({});
    setSelectionModel([]);
  }, [arrayDifference]);

  const handleDeleteClick = (id: GridRowId) => () => {
    const updatedRows = rows.filter((row) => row.id !== id);

    setRows(updatedRows);

    setTimeout(() => {
      handleRowsChange(updatedRows);
    }, 100);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow?.isNew) {
      const updatedRows = rows.filter((row) => row.id !== id);
      setRows(updatedRows);
      handleRowsChange(updatedRows);
    }
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleRowEditStart = (params: GridRowParams, event: MuiEvent<React.SyntheticEvent>) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    event.defaultMuiPrevented = true;
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    const updatedRows = rows.map((row) => (row.id === newRow.id ? updatedRow : row));
    setRows(updatedRows);
    setTimeout(() => {
      handleRowsChange(updatedRows);
    }, 0);

    return updatedRow;
  };

  const handleRowsChange = (newRows: any[]) => {

    if (!onArrayUpdate) {
      return;
    }

    const newArrayData = newRows.map((row, index) => {
      const item: any = {};
      const columns = getColumns();

      columns.forEach(col => {
        let value = row[col.key];

        if (col.type === 'date' && value) {
          if (value instanceof Date) {
            value = moment(value).format('YYYY-MM-DD HH:mm:ss');
          } else if (typeof value === 'string' && value !== 'null') {
            value = moment(value).format('YYYY-MM-DD HH:mm:ss');
          }
        }

        if (value !== null && value !== undefined) {
          item[col.key] = value;
        }
      });

      if (row.Id) {
        item.Id = row.Id;
      } else {
        item.Id = "00000000-0000-0000-0000-000000000000";
      }

      return item;
    });

    const updatedArrayDiff: AditamentoDiferenca = {
      ...arrayDifference,
      newField: JSON.stringify(newArrayData)
    };

    onArrayUpdate(updatedArrayDiff);
  };

  interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
  }

  function EditToolbar(props: EditToolbarProps) {
    const handleClick = () => {
      const id = uuidv4();
      const newIndex = Math.max(...rows.map(r => r.index), -1) + 1;

      const newRow: any = {
        id,
        index: newIndex,
        isNew: true,
        changeType: 'added'
      };

      const columns = getColumns();
      columns.forEach(col => {
        newRow[col.key] = col.type === 'date' ? null : (col.type === 'percentage' ? 0 : null);
      });

      const updatedRows = [...rows, newRow];

      setRows(updatedRows);
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: columns[0]?.key } });

      setTimeout(() => {
        handleRowsChange(updatedRows);
      }, 150);
    };

    const handleDeleteClick = () => {
      const rowsUpdated = rows.filter((row) => !selectionModel.some((sm) => sm === row.id));
      setRows(rowsUpdated);
      setTimeout(() => {
        handleRowsChange(rowsUpdated);
      }, 50);
      setSelectionModel([]);
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          variant="contained"
          size="small"
          onClick={handleClick}
          startIcon={<AddCircle fontSize="small" />}
          disabled={!canEdit}
        >
          {`Novo ${title.slice(0, -1)}`}
        </Button>
        <Button
          color="error"
          size="small"
          onClick={handleDeleteClick}
          disabled={!selectionModel.length || !canEdit}
          startIcon={<Delete fontSize="small" />}
        >
          Excluir selecionados
        </Button>
      </GridToolbarContainer>
    );
  }

  const getColumns = () => {
    switch (arrayType) {
      case 'amortizacoes':
        return [
          { key: 'PercentualAmortizacao', label: '% Amortização', type: 'percentage' },
          { key: 'DataAmortizacao', label: 'Data Amortização', type: 'date' }
        ];
      case 'pagamentojuros':
        return [
          { key: 'Data', label: 'Data Pagamento', type: 'date' }
        ];
      case 'incorporacaojuros':
        return [
          { key: 'Data', label: 'Data Incorporação', type: 'date' }
        ];
      default:
        return [];
    }
  };

  const columns = getColumns();

  const getDataGridColumns = (): GridColumns => {
    const gridColumns: GridColumns = [
      {
        field: 'index',
        headerName: '#',
        width: 80,
        editable: false,
        valueFormatter: (params: GridValueFormatterParams<number>) => {
          return (params.value + 1).toString();
        },
      }
    ];

    columns.forEach(col => {
      if (col.type === 'percentage') {
        gridColumns.push({
          field: col.key,
          headerName: col.label,
          type: 'number',
          width: 120,
          editable: canEdit,
          valueFormatter: (params: GridValueFormatterParams<number>) => {
            return !params.value ? '' : formatNumber(params.value, { minimumFractionDigits: 2, maximumFractionDigits: 8 }) + '%';
          },
        });
      } else if (col.type === 'date') {
        gridColumns.push({
          field: col.key,
          headerName: col.label,
          width: 120,
          type: 'date',
          editable: canEdit,
          valueFormatter: (params: GridValueFormatterParams<Date>) => {
            return !params.value ? '' : moment(params.value).format('DD/MM/YYYY');
          },
        });
      } else {
        gridColumns.push({
          field: col.key,
          headerName: col.label,
          width: 100,
          editable: canEdit,
        });
      }
    });

    gridColumns.push({
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<CheckCircle fontSize="small" />}
              label="Save"
              className="textPrimary"
              onClick={handleSaveClick(id)}
              color="inherit"
              onResize={() => { }}
              onResizeCapture={() => { }}
            />,
            <GridActionsCellItem
              icon={<Cancel fontSize="small" />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
              onResize={() => { }}
              onResizeCapture={() => { }}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<Edit fontSize="small" />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
            disabled={!canEdit}
            onResize={() => { }}
            onResizeCapture={() => { }}
          />,
          <GridActionsCellItem
            icon={<Delete fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
            disabled={!canEdit}
            onResize={() => { }}
            onResizeCapture={() => { }}
          />,
        ];
      },
    });

    return gridColumns;
  };

  const formatValue = (value: any, type: string) => {
    if (value === null || value === undefined) return 'N/A';

    switch (type) {
      case 'date':
        return moment(value).format('DD/MM/YYYY');
      case 'percentage':
        return formatNumber(parseFloat(value), { minimumFractionDigits: 2, maximumFractionDigits: 8 }) + '%';
      default:
        return String(value);
    }
  };

  const summary = {
    added: rows.filter(row => row.changeType === 'added').length,
    removed: beforeRows.filter(row => row.changeType === 'removed').length,
    modified: rows.filter(row => row.changeType === 'modified').length,
    total: Math.max(rows.length, beforeRows.length)
  };

  if (rows.length === 0 && beforeRows.length === 0) {
    return (
      <div className={classes.container}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <CompareIcon />
          {title}
        </Typography>
        <Paper className={classes.emptyState}>
          Nenhuma alteração encontrada neste array.
        </Paper>
      </div>
    );
  }

  return (
    <Paper className={classes.container} elevation={2} style={{ width: '100%', padding: 20 }}>
      {/* Header */}
      <div className={classes.sectionHeader}>
        <CompareIcon />
        <Typography variant="h6" className={classes.sectionTitle}>
          {title}
        </Typography>
      </div>

      {/* Summary */}
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Resumo das alterações:
        </Typography>
        <div>
          {summary.added > 0 && (
            <Chip
              size="small"
              label={`${summary.added} adicionado${summary.added > 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
              className={classes.summaryChip}
              style={{ color: '#4caf50', borderColor: '#4caf50' }}
            />
          )}
          {summary.removed > 0 && (
            <Chip
              size="small"
              label={`${summary.removed} removido${summary.removed > 1 ? 's' : ''}`}
              color="secondary"
              variant="outlined"
              className={classes.summaryChip}
              style={{ color: '#f44336', borderColor: '#f44336' }}
            />
          )}
          {summary.modified > 0 && (
            <Chip
              size="small"
              label={`${summary.modified} modificado${summary.modified > 1 ? 's' : ''}`}
              variant="outlined"
              className={classes.summaryChip}
              style={{ color: '#ff9800', borderColor: '#ff9800' }}
            />
          )}
        </div>
      </Box>

      {/* Comparison Tables */}
      <Box mt={2}>
        <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
          {/* Before Section - Read Only Table */}
          <Grid item xs={12} md={5}>
            <div className={`${classes.sectionHeader} ${classes.beforeHeader}`}>
              <RemoveIcon fontSize="small" />
              Valores Anteriores
            </div>
            <TableContainer className={classes.beforeTableContainer}>
              <Table size="small" stickyHeader>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    {columns.map(col => (
                      <TableCell key={col.key}>{col.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {beforeRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center">
                        <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic', padding: 20 }}>
                          Nenhum valor anterior
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    beforeRows.map(item => (
                      <TableRow
                        key={item.id}
                        className={item.changeType === 'removed' ? classes.rowRemoved : ''}
                      >
                        <TableCell>{item.index + 1}</TableCell>
                        {columns.map(col => (
                          <TableCell key={col.key} className={classes.valueCell}>
                            {formatValue(item[col.key], col.type)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* After Section - Editable DataGrid */}
          <Grid item xs={12} md={7}>
            <div className={`${classes.sectionHeader} ${classes.afterHeader}`}>
              <AddIcon fontSize="small" />
              Valores Novos {canEdit && '(Editável)'}
            </div>
            <div className={classes.dataGridDiv}>
              <DataGridPro
                rows={rows}
                columns={getDataGridColumns()}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                onRowEditStart={handleRowEditStart}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                components={{
                  Toolbar: EditToolbar,
                }}
                componentsProps={{ toolbar: { setRows, setRowModesModel } }}
                experimentalFeatures={{ newEditingApi: true }}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                checkboxSelection={canEdit}
                disableSelectionOnClick
                onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
                selectionModel={selectionModel}
                localeText={localizedTextsMap}
                getRowClassName={(params) => {
                  if (params.row.changeType === 'added') return 'row-added';
                  if (params.row.changeType === 'modified') return 'row-modified';
                  return '';
                }}
                getCellClassName={(params) => {
                  const beforeItem = beforeData[params.row.index];
                  if (beforeItem && beforeItem[params.field] !== params.row[params.field]) {
                    return 'field-changed';
                  }
                  return '';
                }}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};
